import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';

const ROLE_TYPES = [
  { value: '호기심형_진행자', label: '🎤 호기심형 진행자' },
  { value: '정보형_진행자', label: '📋 정보형 진행자' },
  { value: '전문가_게스트', label: '🔬 전문가 게스트' },
  { value: '경험형_게스트', label: '💼 경험형 게스트' },
  { value: '반박형_코호스트', label: '⚡ 반박형 코호스트' },
  { value: '공감형_코호스트', label: '💙 공감형 코호스트' },
  { value: '엔터형_코호스트', label: '😄 엔터형 코호스트' },
  { value: '스토리형_게스트', label: '📖 스토리형 게스트' },
];

const PERSONALITIES = [
  '논리적이고 차분한', '열정적이고 에너지 넘치는', '유머러스하고 가벼운',
  '진지하고 깊이 있는', '친근하고 편안한', '날카롭고 도전적인',
  '따뜻하고 공감 잘 하는', '직설적이고 솔직한',
];

const ACCENT = '#FF6B35';
const emptyForm = { name: '', job: '', roleType: '호기심형_진행자', personality: '', interests: '', defaultEpisodeRole: '' };
const emptyPersona = { version: '', tag: '', catchphrase: '', intro: '', description: '' };

export default function CastManagerPage() {
  const { api } = useAuth();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [form, setForm] = useState(emptyForm);
  const [editMode, setEditMode] = useState(false);
  const [suggestedPersonas, setSuggestedPersonas] = useState([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [userPersonaForm, setUserPersonaForm] = useState(emptyPersona);
  const [showUserPersonaForm, setShowUserPersonaForm] = useState(false);

  const loadMembers = useCallback(async () => {
    try {
      const r = await api.get('/cast');
      setMembers(r.data.members || []);
    } catch (e) {
      console.error('출연진 로드 실패', e);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { loadMembers(); }, [loadMembers]);

  // 등록 (모달)
  const submitNew = async () => {
    if (!form.name.trim()) { alert('이름을 입력하세요'); return; }
    try {
      const r = await api.post('/cast', form);
      setMembers(prev => [r.data.member, ...prev]);
      setForm(emptyForm);
      setShowAddModal(false);
    } catch (e) {
      alert('등록 실패: ' + (e.response?.data?.error || e.message));
    }
  };

  // 출연진 선택
  const selectMember = (m) => {
    setSelected(m);
    setForm({
      name: m.name || '', job: m.job || '', roleType: m.roleType || '호기심형_진행자',
      personality: m.personality || '', interests: m.interests || '',
      defaultEpisodeRole: m.defaultEpisodeRole || '',
    });
    setActiveTab('info');
    setEditMode(false);
    setSuggestedPersonas([]);
    setShowUserPersonaForm(false);
  };

  // 정보 수정
  const saveInfo = async () => {
    try {
      const r = await api.patch(`/cast/${selected._id}`, form);
      setMembers(prev => prev.map(m => m._id === r.data.member._id ? r.data.member : m));
      setSelected(r.data.member);
      setEditMode(false);
    } catch (e) {
      alert('수정 실패: ' + (e.response?.data?.error || e.message));
    }
  };

  // 삭제
  const deleteMember = async () => {
    if (!window.confirm(`'${selected.name}' 출연자를 삭제하시겠습니까?`)) return;
    try {
      await api.delete(`/cast/${selected._id}`);
      setMembers(prev => prev.filter(m => m._id !== selected._id));
      setSelected(null);
    } catch (e) {
      alert('삭제 실패: ' + (e.response?.data?.error || e.message));
    }
  };

  // AI 페르소나 추천
  const suggestPersona = async () => {
    setSuggestLoading(true);
    setSuggestedPersonas([]);
    try {
      const r = await api.post(`/cast/${selected._id}/persona/suggest`);
      setSuggestedPersonas(r.data.personas || []);
    } catch (e) {
      alert('AI 추천 실패: ' + (e.response?.data?.error || e.message));
    }
    setSuggestLoading(false);
  };

  // 페르소나 저장 (AI 추천에서)
  const savePersona = async (persona, source = 'ai', setDefault = false) => {
    try {
      const r = await api.post(`/cast/${selected._id}/persona`, {
        ...persona, source, setDefault,
      });
      setMembers(prev => prev.map(m => m._id === r.data.member._id ? r.data.member : m));
      setSelected(r.data.member);
    } catch (e) {
      alert('저장 실패: ' + (e.response?.data?.error || e.message));
    }
  };

  // 사용자 직접 페르소나 저장
  const submitUserPersona = async () => {
    if (!userPersonaForm.tag && !userPersonaForm.intro) { alert('태그 또는 자기소개를 입력하세요'); return; }
    await savePersona({ ...userPersonaForm, version: userPersonaForm.version || '직접 입력' }, 'user', false);
    setUserPersonaForm(emptyPersona);
    setShowUserPersonaForm(false);
  };

  // 페르소나 기본값 설정
  const setDefaultPersona = async (personaId) => {
    try {
      const r = await api.patch(`/cast/${selected._id}/persona/${personaId}/default`);
      setMembers(prev => prev.map(m => m._id === r.data.member._id ? r.data.member : m));
      setSelected(r.data.member);
    } catch (e) {
      alert('실패: ' + (e.response?.data?.error || e.message));
    }
  };

  // 페르소나 삭제
  const deletePersona = async (personaId) => {
    if (!window.confirm('페르소나를 삭제하시겠습니까?')) return;
    try {
      const r = await api.delete(`/cast/${selected._id}/persona/${personaId}`);
      setMembers(prev => prev.map(m => m._id === r.data.member._id ? r.data.member : m));
      setSelected(r.data.member);
    } catch (e) {
      alert('실패: ' + (e.response?.data?.error || e.message));
    }
  };

  const memberColor = (idx) => {
    const colors = ['#FF6B35', '#00B4D8', '#4CAF50', '#9C6FBF', '#FFB02E', '#E91E63'];
    return colors[idx % colors.length];
  };

  const S = {
    wrap: { minHeight: '100vh', background: '#080C0F', color: '#E8EDF2', fontFamily: "'Noto Sans KR', sans-serif", padding: '40px 32px' },
    header: { marginBottom: 32, display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' },
    title: { fontFamily: "'Bebas Neue', sans-serif", fontSize: 42, letterSpacing: 2, color: ACCENT, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 12 },
    subtitle: { fontSize: 14, color: '#6B7A8D' },

    layout: { display: 'grid', gridTemplateColumns: selected ? '1fr 1.2fr' : '1fr', gap: 24, transition: 'all .3s' },

    // 좌: 그리드
    grid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 16 },
    addCard: {
      padding: 32, borderRadius: 14, border: '2px dashed #1E2A35',
      background: 'transparent', color: '#6B7A8D', fontSize: 14, fontWeight: 600,
      cursor: 'pointer', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', gap: 8,
      minHeight: 180, transition: 'all .15s', fontFamily: "'Noto Sans KR', sans-serif",
    },
    memberCard: (idx, isActive) => ({
      background: '#0E1520', borderRadius: 14,
      border: `1px solid ${isActive ? memberColor(idx) : '#1E2A35'}`,
      borderLeft: `4px solid ${memberColor(idx)}`,
      padding: 20, cursor: 'pointer', transition: 'all .15s',
      display: 'flex', flexDirection: 'column', gap: 8,
    }),
    memberName: (idx) => ({
      fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, letterSpacing: 1,
      color: memberColor(idx), marginBottom: 2,
    }),
    memberJob: { fontSize: 12, color: '#6B7A8D' },
    memberRole: (idx) => ({
      fontSize: 11, color: memberColor(idx), padding: '3px 8px',
      background: `${memberColor(idx)}15`, borderRadius: 100,
      alignSelf: 'flex-start', marginTop: 4,
    }),
    memberPersonaCount: { fontSize: 11, color: '#6B7A8D', marginTop: 8 },

    // 우: 상세 패널
    detailPanel: {
      background: '#0E1520', border: '1px solid #1E2A35', borderRadius: 16,
      padding: 28, maxHeight: 'calc(100vh - 160px)', overflowY: 'auto',
      position: 'sticky', top: 32,
    },
    detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },

    tabBar: { display: 'flex', gap: 0, marginBottom: 24, borderBottom: '1px solid #1E2A35' },
    tab: (active) => ({
      padding: '10px 20px', cursor: 'pointer', fontSize: 13, fontWeight: 600,
      color: active ? ACCENT : '#6B7A8D',
      borderBottom: `2px solid ${active ? ACCENT : 'transparent'}`,
      transition: 'all .15s',
    }),

    label: { fontSize: 12, fontWeight: 600, color: '#6B7A8D', marginBottom: 6, display: 'block' },
    input: {
      width: '100%', padding: '10px 14px', background: '#080C0F',
      border: '1px solid #1E2A35', borderRadius: 8, color: '#E8EDF2',
      fontSize: 14, outline: 'none', boxSizing: 'border-box',
      fontFamily: "'Noto Sans KR', sans-serif",
    },
    select: {
      width: '100%', padding: '10px 14px', background: '#080C0F',
      border: '1px solid #1E2A35', borderRadius: 8, color: '#E8EDF2',
      fontSize: 14, outline: 'none', cursor: 'pointer',
      fontFamily: "'Noto Sans KR', sans-serif",
    },
    field: { marginBottom: 14 },

    tagGrid: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 6 },
    tag: (selected) => ({
      padding: '5px 12px', borderRadius: 100, cursor: 'pointer', fontSize: 12,
      border: `1px solid ${selected ? ACCENT : '#1E2A35'}`,
      background: selected ? `${ACCENT}20` : 'transparent',
      color: selected ? ACCENT : '#6B7A8D', transition: 'all .15s',
    }),

    btnPrimary: {
      padding: '10px 20px', background: ACCENT, border: 'none', borderRadius: 8,
      color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
      fontFamily: "'Noto Sans KR', sans-serif",
    },
    btnSecondary: {
      padding: '10px 20px', background: 'transparent', border: '1px solid #1E2A35',
      borderRadius: 8, color: '#6B7A8D', fontSize: 13, cursor: 'pointer',
      fontFamily: "'Noto Sans KR', sans-serif",
    },
    btnDanger: {
      padding: '8px 16px', background: 'transparent', border: '1px solid #E9434340',
      borderRadius: 8, color: '#E94343', fontSize: 12, cursor: 'pointer',
      fontFamily: "'Noto Sans KR', sans-serif",
    },
    btnAccent: {
      padding: '10px 16px', background: `${ACCENT}15`, border: `1px solid ${ACCENT}40`,
      borderRadius: 8, color: ACCENT, fontSize: 13, fontWeight: 600, cursor: 'pointer',
      fontFamily: "'Noto Sans KR', sans-serif",
    },

    personaCard: (highlighted) => ({
      padding: 16, borderRadius: 12, marginBottom: 10,
      background: highlighted ? `${ACCENT}10` : '#080C0F',
      border: `1px solid ${highlighted ? ACCENT : '#1E2A35'}`,
    }),
    savedPersonaCard: (isDefault) => ({
      padding: 14, borderRadius: 10, marginBottom: 8, background: '#080C0F',
      border: `1px solid ${isDefault ? `${ACCENT}80` : '#1E2A35'}`,
    }),

    // 모달
    modalBackdrop: {
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', zIndex: 100,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    modal: {
      background: '#0E1520', border: '1px solid #1E2A35', borderRadius: 16,
      padding: 32, width: 540, maxWidth: '90vw', maxHeight: '85vh', overflowY: 'auto',
    },
  };

  return (
    <div style={S.wrap}>
      <link href="https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Noto+Sans+KR:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <div style={S.header}>
        <div>
          <div style={S.title}><span>👥</span> CAST CREW</div>
          <div style={S.subtitle}>출연진 프로필 + AI 페르소나 관리 · 등록한 출연진은 팟캐스트 대본 생성 시 불러올 수 있습니다</div>
        </div>
      </div>

      <div style={S.layout}>
        {/* 좌: 출연진 그리드 */}
        <div>
          <div style={S.grid}>
            <div style={S.addCard} onClick={() => { setForm(emptyForm); setShowAddModal(true); }}>
              <div style={{ fontSize: 32, marginBottom: 4 }}>+</div>
              <div>새 출연자 등록</div>
            </div>
            {loading ? (
              <div style={{ color: '#6B7A8D', padding: 20 }}>로딩 중...</div>
            ) : members.map((m, idx) => {
              const isActive = selected?._id === m._id;
              const defaultPersona = m.personas?.find(p => p.isDefault) || m.personas?.[0];
              return (
                <div key={m._id} style={S.memberCard(idx, isActive)} onClick={() => selectMember(m)}>
                  <div style={S.memberName(idx)}>{m.name}</div>
                  <div style={S.memberJob}>{m.job || '-'}</div>
                  <div style={S.memberRole(idx)}>
                    {ROLE_TYPES.find(r => r.value === m.roleType)?.label || m.roleType}
                  </div>
                  {defaultPersona && (
                    <div style={{ fontSize: 11, color: '#C8D0DA', marginTop: 6, fontStyle: 'italic' }}>
                      "{defaultPersona.tag || defaultPersona.version}"
                    </div>
                  )}
                  <div style={S.memberPersonaCount}>
                    ✨ 페르소나 {m.personas?.length || 0}개
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 우: 상세 패널 */}
        {selected && (
          <div style={S.detailPanel}>
            <div style={S.detailHeader}>
              <div>
                <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 28, color: ACCENT, letterSpacing: 1 }}>
                  {selected.name}
                </div>
                <div style={{ fontSize: 12, color: '#6B7A8D' }}>{selected.job || '-'}</div>
              </div>
              <button style={S.btnSecondary} onClick={() => setSelected(null)}>닫기</button>
            </div>

            <div style={S.tabBar}>
              <div style={S.tab(activeTab === 'info')} onClick={() => setActiveTab('info')}>📋 기본 정보</div>
              <div style={S.tab(activeTab === 'persona')} onClick={() => setActiveTab('persona')}>
                ✨ 페르소나 ({selected.personas?.length || 0})
              </div>
            </div>

            {/* 기본 정보 탭 */}
            {activeTab === 'info' && (
              <div>
                <div style={S.field}>
                  <label style={S.label}>이름</label>
                  <input style={S.input} value={form.name} disabled={!editMode}
                    onChange={e => setForm({ ...form, name: e.target.value })} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>직업 / 현재 하는 일</label>
                  <input style={S.input} value={form.job} disabled={!editMode}
                    onChange={e => setForm({ ...form, job: e.target.value })} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>역할 유형</label>
                  <select style={S.select} value={form.roleType} disabled={!editMode}
                    onChange={e => setForm({ ...form, roleType: e.target.value })}>
                    {ROLE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                </div>
                <div style={S.field}>
                  <label style={S.label}>성격 / 말투</label>
                  {editMode ? (
                    <div style={S.tagGrid}>
                      {PERSONALITIES.map(p => (
                        <div key={p} style={S.tag(form.personality === p)}
                          onClick={() => setForm({ ...form, personality: form.personality === p ? '' : p })}>
                          {p}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div style={{ ...S.input, padding: '10px 14px' }}>{form.personality || '-'}</div>
                  )}
                </div>
                <div style={S.field}>
                  <label style={S.label}>관심분야 / 전문 영역</label>
                  <input style={S.input} value={form.interests} disabled={!editMode}
                    onChange={e => setForm({ ...form, interests: e.target.value })} />
                </div>
                <div style={S.field}>
                  <label style={S.label}>기본 에피소드 역할</label>
                  <input style={S.input} value={form.defaultEpisodeRole} disabled={!editMode}
                    onChange={e => setForm({ ...form, defaultEpisodeRole: e.target.value })}
                    placeholder="예: 전문 지식 제공" />
                </div>

                <div style={{ display: 'flex', gap: 8, marginTop: 24, justifyContent: 'space-between' }}>
                  <button style={S.btnDanger} onClick={deleteMember}>🗑️ 출연자 삭제</button>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {editMode ? (
                      <>
                        <button style={S.btnSecondary} onClick={() => { selectMember(selected); }}>취소</button>
                        <button style={S.btnPrimary} onClick={saveInfo}>저장</button>
                      </>
                    ) : (
                      <button style={S.btnAccent} onClick={() => setEditMode(true)}>✏️ 수정</button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* 페르소나 탭 */}
            {activeTab === 'persona' && (
              <div>
                {/* 저장된 페르소나 목록 */}
                {selected.personas?.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#E8EDF2', marginBottom: 12 }}>
                      💾 저장된 페르소나
                    </div>
                    {selected.personas.map(p => (
                      <div key={p._id} style={S.savedPersonaCard(p.isDefault)}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: '#E8EDF2' }}>
                              {p.isDefault && '⭐ '}{p.version}
                            </div>
                            <div style={{
                              fontSize: 10, padding: '2px 6px', borderRadius: 4,
                              background: p.source === 'ai' ? '#9C6FBF20' : '#4CAF5020',
                              color: p.source === 'ai' ? '#9C6FBF' : '#4CAF50',
                            }}>
                              {p.source === 'ai' ? 'AI' : '직접'}
                            </div>
                          </div>
                          <div style={{ display: 'flex', gap: 4 }}>
                            {!p.isDefault && (
                              <button style={{ ...S.btnSecondary, padding: '4px 10px', fontSize: 11 }}
                                onClick={() => setDefaultPersona(p._id)}>기본값</button>
                            )}
                            <button style={{ ...S.btnDanger, padding: '4px 10px', fontSize: 11 }}
                              onClick={() => deletePersona(p._id)}>삭제</button>
                          </div>
                        </div>
                        {p.tag && <div style={{ fontSize: 12, color: ACCENT, marginBottom: 2 }}>"{p.tag}"</div>}
                        {p.intro && <div style={{ fontSize: 11, color: '#C8D0DA', fontStyle: 'italic', marginBottom: 2 }}>{p.intro}</div>}
                        {p.catchphrase && <div style={{ fontSize: 11, color: '#6B7A8D' }}>말버릇: "{p.catchphrase}"</div>}
                      </div>
                    ))}
                  </div>
                )}

                {/* AI 추천 */}
                <div style={{ marginBottom: 24 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#E8EDF2' }}>🤖 AI 추천</div>
                    <button style={S.btnAccent} onClick={suggestPersona} disabled={suggestLoading}>
                      {suggestLoading ? '🔄 분석 중...' : '✨ AI 페르소나 3가지 추천'}
                    </button>
                  </div>

                  {suggestedPersonas.length > 0 && (
                    <div>
                      {suggestedPersonas.map((p, pi) => (
                        <div key={pi} style={S.personaCard(false)}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <div style={{ fontSize: 14, fontWeight: 700, color: '#E8EDF2' }}>
                              {['🔥','💡','📖'][pi]} {p.version}
                            </div>
                            <div style={{ fontSize: 11, color: '#6B7A8D' }}>{p.strength}</div>
                          </div>
                          <div style={{ fontSize: 12, color: ACCENT, marginBottom: 4 }}>"{p.tag}"</div>
                          <div style={{ fontSize: 12, color: '#C8D0DA', marginBottom: 4, fontStyle: 'italic' }}>
                            {p.intro}
                          </div>
                          <div style={{ fontSize: 11, color: '#6B7A8D', marginBottom: 10 }}>
                            말버릇: "{p.catchphrase}"
                          </div>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button style={{ ...S.btnSecondary, padding: '6px 12px', fontSize: 11 }}
                              onClick={() => savePersona(p, 'ai', false)}>+ 저장</button>
                            <button style={{ ...S.btnAccent, padding: '6px 12px', fontSize: 11 }}
                              onClick={() => savePersona(p, 'ai', true)}>⭐ 기본값으로 저장</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* 직접 입력 */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, color: '#E8EDF2' }}>✏️ 직접 입력</div>
                    {!showUserPersonaForm && (
                      <button style={S.btnSecondary}
                        onClick={() => setShowUserPersonaForm(true)}>+ 추가</button>
                    )}
                  </div>

                  {showUserPersonaForm && (
                    <div style={S.personaCard(false)}>
                      <div style={S.field}>
                        <label style={S.label}>버전명</label>
                        <input style={S.input} value={userPersonaForm.version}
                          onChange={e => setUserPersonaForm({ ...userPersonaForm, version: e.target.value })}
                          placeholder="예: 친근파, 단호파" />
                      </div>
                      <div style={S.field}>
                        <label style={S.label}>한 줄 캐릭터 태그</label>
                        <input style={S.input} value={userPersonaForm.tag}
                          onChange={e => setUserPersonaForm({ ...userPersonaForm, tag: e.target.value })}
                          placeholder="예: 30년 임상의 살아있는 성장 백과사전" />
                      </div>
                      <div style={S.field}>
                        <label style={S.label}>자기소개</label>
                        <input style={S.input} value={userPersonaForm.intro}
                          onChange={e => setUserPersonaForm({ ...userPersonaForm, intro: e.target.value })}
                          placeholder="방송용 자기소개 멘트" />
                      </div>
                      <div style={S.field}>
                        <label style={S.label}>시그니처 말투</label>
                        <input style={S.input} value={userPersonaForm.catchphrase}
                          onChange={e => setUserPersonaForm({ ...userPersonaForm, catchphrase: e.target.value })}
                          placeholder="예: 솔직히 말씀드리면..." />
                      </div>
                      <div style={S.field}>
                        <label style={S.label}>대본 연출 가이드 (AI용)</label>
                        <input style={S.input} value={userPersonaForm.description}
                          onChange={e => setUserPersonaForm({ ...userPersonaForm, description: e.target.value })}
                          placeholder="대본에서 어떻게 묘사할지" />
                      </div>
                      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button style={S.btnSecondary} onClick={() => { setShowUserPersonaForm(false); setUserPersonaForm(emptyPersona); }}>취소</button>
                        <button style={S.btnPrimary} onClick={submitUserPersona}>저장</button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* 새 출연자 등록 모달 */}
      {showAddModal && (
        <div style={S.modalBackdrop} onClick={() => setShowAddModal(false)}>
          <div style={S.modal} onClick={e => e.stopPropagation()}>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 24, color: ACCENT, letterSpacing: 1, marginBottom: 20 }}>
              ➕ 새 출연자 등록
            </div>
            <div style={S.field}>
              <label style={S.label}>이름 *</label>
              <input style={S.input} value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })}
                placeholder="예: 박승찬" />
            </div>
            <div style={S.field}>
              <label style={S.label}>직업 / 현재 하는 일</label>
              <input style={S.input} value={form.job}
                onChange={e => setForm({ ...form, job: e.target.value })}
                placeholder="예: 하이키한의원 원장" />
            </div>
            <div style={S.field}>
              <label style={S.label}>역할 유형</label>
              <select style={S.select} value={form.roleType}
                onChange={e => setForm({ ...form, roleType: e.target.value })}>
                {ROLE_TYPES.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
              </select>
            </div>
            <div style={S.field}>
              <label style={S.label}>성격</label>
              <div style={S.tagGrid}>
                {PERSONALITIES.map(p => (
                  <div key={p} style={S.tag(form.personality === p)}
                    onClick={() => setForm({ ...form, personality: form.personality === p ? '' : p })}>
                    {p}
                  </div>
                ))}
              </div>
            </div>
            <div style={S.field}>
              <label style={S.label}>관심분야</label>
              <input style={S.input} value={form.interests}
                onChange={e => setForm({ ...form, interests: e.target.value })}
                placeholder="예: 아동 성장, 한방 치료" />
            </div>
            <div style={S.field}>
              <label style={S.label}>기본 에피소드 역할</label>
              <input style={S.input} value={form.defaultEpisodeRole}
                onChange={e => setForm({ ...form, defaultEpisodeRole: e.target.value })}
                placeholder="예: 전문 지식 제공" />
            </div>

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
              <button style={S.btnSecondary} onClick={() => setShowAddModal(false)}>취소</button>
              <button style={S.btnPrimary} onClick={submitNew}>등록</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
